import { thumbnailCache, useLazyGetProjectThumbnailQuery } from '@store/api/projectEndpoints';
import { gray } from '@theme/primitives';
import { useEffect, useRef, useState } from 'react';
import { CubeIcon } from 'react-line-awesome';

interface ThumbnailProps {
  projectId: string;
  /** Size of the icon drawn for a project that has no thumbnail; a picture fills the box it is put in. */
  iconSize: number;
}

const Thumbnail = ({ projectId, iconSize }: ThumbnailProps) => {
  const boxRef = useRef<HTMLDivElement>(null);
  const [isInView, setIsInView] = useState(false);
  // `undefined` while not known yet, `null` for a project that has no thumbnail. Started from the
  // cache, so switching between the two views does not blank every picture for a moment.
  const [thumbnailUrl, setThumbnailUrl] = useState(() => thumbnailCache.get(projectId));
  const [getProjectThumbnail] = useLazyGetProjectThumbnailQuery();

  // The listing has no pages, so a thumbnail is asked for only once its box is on the
  // screen: a folder of a few hundred projects would otherwise send that many requests at
  // once into the API's request limit.
  useEffect(() => {
    const box = boxRef.current;
    if (!box) return;

    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        setIsInView(true);
        observer.disconnect();
      }
    });

    observer.observe(box);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const fetchThumbnail = async () => {
      const cachedThumbnail = thumbnailCache.get(projectId);

      if (cachedThumbnail !== undefined) return setThumbnailUrl(cachedThumbnail);

      // From the query's cache when it holds the answer, so switching views while a thumbnail
      // is on its way does not ask for it again.
      const { data, error } = await getProjectThumbnail(projectId, true);

      setThumbnailUrl(data ?? null);

      // Only a 404 means the project has no thumbnail. A request the rate limit refused or the
      // network lost is asked again the next time the project is listed.
      if (data || (error && 'status' in error && error.status === 404)) thumbnailCache.set(projectId, data ?? null);
    };

    if (isInView) fetchThumbnail();

  }, [projectId, getProjectThumbnail, isInView]);

  return (
    <div
      ref={boxRef}
      style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      {thumbnailUrl === null && <CubeIcon style={{ fontSize: iconSize, color: gray[400] }} />}
      {thumbnailUrl && <img src={thumbnailUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
    </div>
  );
};

export default Thumbnail;
